// Endpoint functions for the VAISP operations module. Each returns a parsed
// view model; each throws `AppError` via the axios interceptor.

import { adminApi as api } from '@core/api/httpClient'
import type { PaginatedResponse } from '@shared/types/pagination'

import { VASP_ENDPOINT_PATHS } from './vaspEndpointPaths'
import {
  parseVaspBatchWebhookReplay,
  parseVaspDocumentReMirror,
  parseVaspFailedWebhookEvents,
  parseVaspWebhookEventPage,
  parseVaspHoldings,
  parseVaspTitleDeedDownload,
  parseVaspWebhookReplay,
} from './vaspParser'
import type {
  ReMirrorHoldingDocumentsInput,
  ReplayVaspWebhookEventInput,
  VaspBatchWebhookReplay,
  VaspDocumentReMirror,
  VaspFailedWebhookEvents,
  VaspHoldings,
  VaspTitleDeedDownload,
  VaspWebhookEvent,
  VaspWebhookEventsQuery,
  VaspWebhookReplay,
} from '../types/vaspTypes'

// Blank and undefined filters are dropped rather than sent. An empty string on
// the wire is a filter for the empty string, which matches nothing — a cleared
// field has to mean "no constraint".
function toDefinedParams(query: VaspWebhookEventsQuery): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
  ) as Record<string, string | number>
}

/** Every holding Ctrl-Alt has mirrored for one offering, with its documents. */
export const getVaspHoldings = async (offeringId: string): Promise<VaspHoldings> => {
  const { data } = await api.get(VASP_ENDPOINT_PATHS.HOLDINGS, { params: { offeringId } })
  return parseVaspHoldings(data?.data ?? data)
}

/**
 * Mints a presigned link for one holding's fractional title deed.
 *
 * A GET behind a mutation hook on purpose — the URL expires, so it must never
 * enter the query cache where a later click would replay a dead link.
 */
export const getVaspTitleDeedDownloadUrl = async (
  holdingId: string,
): Promise<VaspTitleDeedDownload> => {
  const { data } = await api.get(VASP_ENDPOINT_PATHS.HOLDING_TITLE_DEED_DOWNLOAD_URL(holdingId))
  return parseVaspTitleDeedDownload(data?.data ?? data)
}

/**
 * Asks Ctrl-Alt for a holding's documents again.
 *
 * `force` is sent only when the caller explicitly passes it: at the retry bound
 * admin-service refuses the run, and that refusal is the safeguard — not a
 * speed bump to route around by defaulting the flag on.
 */
export const reMirrorHoldingDocuments = async ({
  holdingId,
  force,
}: ReMirrorHoldingDocumentsInput): Promise<VaspDocumentReMirror> => {
  const { data } = await api.post(
    VASP_ENDPOINT_PATHS.HOLDING_DOCUMENTS_RE_MIRROR(holdingId),
    force ? { force: true } : {},
  )
  return parseVaspDocumentReMirror(data?.data ?? data)
}

/**
 * One page of the inbound Ctrl-Alt event log, newest first.
 *
 * Unlike the `/failed` queue this covers every status, which is the only way
 * an `ignored` event — one the receiver did not recognise when it arrived —
 * is reachable at all. Blank filters are dropped rather than sent empty, so a
 * cleared field means "no constraint" instead of "match the empty string".
 */
export const getVaspWebhookEvents = async (
  query: VaspWebhookEventsQuery,
): Promise<PaginatedResponse<VaspWebhookEvent>> => {
  const { data } = await api.get(VASP_ENDPOINT_PATHS.WEBHOOK_EVENTS, {
    params: toDefinedParams(query),
  })
  return parseVaspWebhookEventPage(data?.data ?? data)
}

/** Inbound Ctrl-Alt webhook events that failed processing, newest first. */
export const getFailedVaspWebhookEvents = async (
  limit: number,
): Promise<VaspFailedWebhookEvents> => {
  const { data } = await api.get(VASP_ENDPOINT_PATHS.WEBHOOK_EVENTS_FAILED, { params: { limit } })
  return parseVaspFailedWebhookEvents(data?.data ?? data)
}

/**
 * Replays one failed event.
 *
 * Resolves 2xx for a refusal and for a re-failure as well as for a dispatch —
 * the resolved promise says only that the request was accepted. Callers must
 * read the outcome through `replayOutcomeView`.
 */
export const replayVaspWebhookEvent = async ({
  id,
  force,
}: ReplayVaspWebhookEventInput): Promise<VaspWebhookReplay> => {
  const { data } = await api.post(
    VASP_ENDPOINT_PATHS.WEBHOOK_EVENT_REPLAY(id),
    force ? { force: true } : {},
  )
  return parseVaspWebhookReplay(data?.data ?? data)
}

/** Replays up to `limit` failed events. Same 2xx caveat as the single replay. */
export const replayFailedVaspWebhookEvents = async (
  limit: number,
): Promise<VaspBatchWebhookReplay> => {
  const { data } = await api.post(VASP_ENDPOINT_PATHS.WEBHOOK_EVENTS_REPLAY_FAILED, { limit })
  return parseVaspBatchWebhookReplay(data?.data ?? data)
}
